import React from 'react';
import { ArrowLeft, Mail, MessageCircle, HelpCircle, Bug, Lightbulb, Heart } from 'lucide-react';
import { scrollToTop } from '../utils/scrollToTop';

interface ContactUsProps {
  onBack?: () => void;
}

export function ContactUs({ onBack }: ContactUsProps) {
  const handleBack = () => {
    scrollToTop();
    if (onBack) return onBack();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="container mx-auto px-4 pt-8">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>
      </div>
      
      <div className="container mx-auto px-4 pb-16 max-w-4xl">
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <MessageCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl font-bold mb-6 text-gray-900">
            Contact Us
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We'd love to hear from you! Get in touch with questions, feedback, or suggestions.
          </p>
        </div>

        {/* Contact Methods */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Email Support</h3>
                <p className="text-blue-700">Get help via email</p>
              </div>
            </div>
            <p className="text-gray-600 mb-4">
              For technical support, feature requests, or general questions, send us an email.
            </p>
            <div className="bg-blue-500/20 rounded-lg p-4">
              <p className="text-blue-700 font-semibold">asksniprush@gmail.com</p>
              <p className="text-base text-gray-600 mt-1">
                We typically respond within 24 hours
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                <HelpCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">General Inquiries</h3>
                <p className="text-green-700">Business & partnerships</p>
              </div>
            </div>
            <p className="text-gray-600 mb-4">
              For business inquiries, partnerships, or media requests.
            </p>
            <div className="bg-green-500/20 rounded-lg p-4">
              <p className="text-green-700 font-semibold">asksniprush@gmail.com</p>
              <p className="text-base text-gray-600 mt-1">
                For business and partnership inquiries
              </p>
            </div>
          </div>
        </div>

        {/* Contact Categories */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 mb-12">
          <h2 className="text-2xl font-bold mb-8 text-center">How Can We Help?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bug className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Bug Reports</h3>
              <p className="text-base text-gray-600">
                Found an issue? Let us know so we can fix it quickly.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lightbulb className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Feature Requests</h3>
              <p className="text-base text-gray-600">
                Have an idea for a new feature? We'd love to hear it!
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Feedback</h3>
              <p className="text-base text-gray-600">
                Share your experience and help us improve.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 mb-12">
          <h2 className="text-2xl font-bold mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-2 text-blue-700">Is my data really private?</h3>
              <p className="text-gray-300">
                Yes! All processing happens in your browser. Your files never leave your device. 
                We use client-side technologies like FFmpeg.wasm and Whisper AI that run locally.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold mb-2 text-blue-700">Do I need to create an account?</h3>
              <p className="text-gray-300">
                No account required! You can start using Snip Rush immediately without any registration. 
                Your privacy is protected by design.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold mb-2 text-blue-700">What file formats are supported?</h3>
              <p className="text-gray-300">
                We support most common video formats (MP4, MOV, AVI, etc.) and image formats (JPG, PNG, GIF). 
                The exact formats depend on your browser's capabilities.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold mb-2 text-blue-700">Is there a file size limit?</h3>
              <p className="text-gray-300 mb-3">
                Yes, there are several technical limitations based on browser and device capabilities:
              </p>
              <div className="bg-blue-500/20 rounded-lg p-4">
                <h4 className="font-semibold mb-2 text-blue-700">Current Limitations:</h4>
                <ul className="text-base text-gray-300 space-y-1">
                  <li>• <strong>Maximum file size:</strong> 2GB (browser memory limit)</li>
                  <li>• <strong>Recommended size:</strong> Under 500MB for optimal performance</li>
                  <li>• <strong>Video duration:</strong> Up to 10 minutes for smooth processing</li>
                  <li>• <strong>Resolution:</strong> Up to 4K (3840×2160) supported</li>
                  <li>• <strong>Formats:</strong> MP4, MOV, AVI, WebM, MKV</li>
                </ul>
                <p className="text-base text-gray-600 mt-2">
                  <strong>Note:</strong> Larger files may cause browser crashes or slow processing. 
                  For files over 1GB, consider trimming your video first.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Response Time */}
        <div className="bg-blue-600 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Quick Response Times</h2>
          <p className="text-xl text-white mb-6">
            We're committed to providing excellent support. Here's what you can expect:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="font-semibold mb-1">Bug Reports</h3>
              <p className="text-base text-white">Response within 24 hours</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">💡</span>
              </div>
              <h3 className="font-semibold mb-1">Feature Requests</h3>
              <p className="text-base text-white">Review within 48 hours</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">🤝</span>
              </div>
              <h3 className="font-semibold mb-1">General Inquiries</h3>
              <p className="text-base text-white">Response within 24 hours</p>
            </div>
          </div>
        </div>

        <div className="text-center mt-12 text-base text-gray-600">
          <p>We're here to help make your video editing experience amazing!</p>
          <p className="mt-2">
            For urgent issues, please include "URGENT" in your email subject line.
          </p>
        </div>
      </div>
    </div>
  );
}
